import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { complaintAPI, userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ArrowLeft, Clock, User, CheckCircle, MessageSquare, AlertCircle, X, ImagePlus, Film } from 'lucide-react';
import { Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useViewMode } from '../hooks/useViewMode';
import { AppSheet, SheetRow } from '../components/common/AppViewKit';
import useDarkReady from '../theme/useDarkReady';

const PRIORITY_COLOR = { low: 'var(--primary)', medium: '#d97706', high: '#ea580c', critical: '#dc2626' };
const VIDEO_EXT = /\.(mp4|mov|webm|m4v|3gp|avi)$/i;

function AttachmentsCard({ attachments }) {
  if (!attachments?.length) return null;
  return (
    <div className="card mb-4">
      <div className="card-header"><h3>Photo / Video</h3></div>
      <div className="card-body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {attachments.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
              {VIDEO_EXT.test(url) ? (
                <video src={url} controls style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--cb-000000)' }} />
              ) : (
                <img src={url} alt={`Attachment ${i + 1}`} style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// Small photo/video picker shared by the Add Comment and Update Status forms below — up
// to 5 files, matching the backend's complaintMediaUpload limit.
function ImagePicker({ images, setImages }) {
  const addImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - images.length);
    if (!files.length) return;
    setImages(list => [...list, ...files.map(file => ({ file, preview: URL.createObjectURL(file), isVideo: file.type.startsWith('video/') }))]);
    e.target.value = '';
  };
  const removeImage = (i) => setImages(list => list.filter((_, idx) => idx !== i));

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
      {images.map((img, i) => (
        <div key={i} style={{ position: 'relative', width: 48, height: 48 }}>
          {img.isVideo ? (
            <>
              <video src={img.preview} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, background: 'var(--cb-000000)' }} />
              <Film size={14} color="var(--c-ffffff)" style={{ position: 'absolute', bottom: 3, left: 3, filter: 'drop-shadow(0 0 2px rgba(0,0,0,.8))' }} />
            </>
          ) : (
            <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
          )}
          <button type="button" onClick={() => removeImage(i)}
            style={{ position: 'absolute', top: -6, right: -6, background: 'rgba(0,0,0,.6)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={11} color="var(--c-ffffff)" />
          </button>
        </div>
      ))}
      {images.length < 5 && (
        <label style={{ width: 48, height: 48, border: '1px dashed var(--border)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
          <ImagePlus size={16} />
          <input type="file" accept="image/*,video/*" multiple hidden onChange={addImages} />
        </label>
      )}
    </div>
  );
}

export default function ComplaintDetailPage() {
  useDarkReady();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isStaffOrAdmin, user } = useAuth();
  const [complaint, setComplaint] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [commentImages, setCommentImages] = useState([]);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [customerRemark, setCustomerRemark] = useState('');
  const [statusImages, setStatusImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  const [sheet, setSheet] = useState(null); // app view: 'assign' | 'resolve'

  const load = async () => {
    try {
      const [cd] = await Promise.all([complaintAPI.getById(id)]);
      setComplaint(cd.data.data);
    } catch { toast.error('Ticket not found'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (isStaffOrAdmin) {
      userAPI.getAll().then(({ data }) => setStaff(data.data)).catch(() => {});
    }
  }, [isStaffOrAdmin]);

  const handleAssign = async (staffId) => {
    try {
      await complaintAPI.assign(id, { assignedTo: staffId });
      setSheet(null);
      toast.success('Ticket assigned');
      load();
    } catch { toast.error('Failed to assign'); }
  };

  const handleStatusUpdate = async (status) => {
    setSubmitting(true);
    try {
      let payload = { status, resolutionNotes, customerRemark };
      if (statusImages.length) {
        payload = new FormData();
        payload.append('status', status);
        payload.append('resolutionNotes', resolutionNotes);
        payload.append('customerRemark', customerRemark);
        statusImages.forEach(img => payload.append('media', img.file));
      }
      await complaintAPI.updateStatus(id, payload);
      setStatusImages([]);
      setSheet(null);
      toast.success(`Ticket ${status}`);
      load();
    } catch { toast.error('Failed to update status'); }
    finally { setSubmitting(false); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      let payload = { comment };
      if (commentImages.length) {
        payload = new FormData();
        payload.append('comment', comment);
        commentImages.forEach(img => payload.append('media', img.file));
      }
      await complaintAPI.addComment(id, payload);
      setComment('');
      setCommentImages([]);
      toast.success('Comment added');
      load();
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!complaint) return <div className="empty-state"><h3>Ticket not found</h3></div>;

  const pColor = PRIORITY_COLOR[complaint.priority] || 'var(--primary)';
  const slaBreach = complaint.slaDeadline && new Date() > new Date(complaint.slaDeadline) && !['resolved', 'closed'].includes(complaint.status);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/complaints')}><ArrowLeft size={14} /></button>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{complaint.ticketNumber}</span>
            <span className={`badge badge-${complaint.status}`} style={{ textTransform: 'capitalize' }}>
              {complaint.status?.replace(/_/g, ' ')}
            </span>
            <span className="badge" style={{ background: `${pColor}20`, color: pColor, textTransform: 'capitalize' }}>
              {complaint.priority}
            </span>
            {slaBreach && <span className="badge badge-danger">SLA Breached</span>}
          </div>
          <p style={{ fontSize: '.9rem', fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>{complaint.title}</p>
        </div>
      </div>

      {isApp && (
        <div style={{ marginBottom: 12 }}>
          <div className="card" style={{ padding: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link to={`/customers/${complaint.customer?._id}`} style={{ color: 'var(--primary)', fontWeight: 700 }}>{complaint.customer?.name}</Link>
                <div style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
                  {[complaint.customer?.address?.area, complaint.customer?.broadband?.houseNumber || complaint.customer?.catv?.houseNumber, complaint.customer?.mobile].filter(Boolean).join(' · ')}
                </div>
              </div>
              {complaint.customer?.mobile && (
                <a href={`tel:${complaint.customer.mobile}`} className="btn btn-outline" style={{ minHeight: 44 }} aria-label="Call customer"><Phone size={16} /> Call</a>
              )}
            </div>
          </div>
          {isStaffOrAdmin && (
            <>
              <button type="button" className="btn btn-secondary w-full" style={{ justifyContent: 'space-between', minHeight: 46, marginBottom: 8 }} onClick={() => setSheet('assign')}>
                <span><User size={14} style={{ display: 'inline', marginRight: 6 }} />Assigned to</span>
                <strong>{complaint.assignedTo?.name || 'Unassigned'}</strong>
              </button>
              {!['resolved', 'closed'].includes(complaint.status) && (
                <div style={{ display: 'flex', gap: 8 }}>
                  {complaint.status === 'open' && (
                    <button type="button" className="btn btn-warning" style={{ flex: 1, minHeight: 46 }} disabled={submitting} onClick={() => handleStatusUpdate('in_progress')}>Start work</button>
                  )}
                  <button type="button" className="btn btn-success" style={{ flex: 1, minHeight: 46 }} onClick={() => setSheet('resolve')}><CheckCircle size={15} /> Resolve</button>
                </div>
              )}
              {complaint.status === 'resolved' && (
                <button type="button" className="btn btn-secondary w-full" style={{ minHeight: 46 }} disabled={submitting} onClick={() => handleStatusUpdate('closed')}>Close ticket</button>
              )}
            </>
          )}
        </div>
      )}

      {sheet === 'assign' && (
        <AppSheet title="Assign to" onClose={() => setSheet(null)}>
          <SheetRow label="Unassigned" onClick={() => handleAssign('')} />
          {staff.map(s => <SheetRow key={s._id} label={`${s.name} (${s.role})${s._id === complaint.assignedTo?._id ? ' ✓' : ''}`} onClick={() => handleAssign(s._id)} />)}
        </AppSheet>
      )}
      {sheet === 'resolve' && (
        <AppSheet title="Resolve ticket" onClose={() => setSheet(null)}>
          <div className="form-group">
            <label className="form-label">What was done <span className="required">*</span></label>
            <textarea className="form-control" rows={3} value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} placeholder="Internal note" />
          </div>
          <div className="form-group">
            <label className="form-label">Message to customer (WhatsApp)</label>
            <textarea className="form-control" rows={2} value={customerRemark} onChange={e => setCustomerRemark(e.target.value)} placeholder="Optional" />
          </div>
          <ImagePicker images={statusImages} setImages={setStatusImages} />
          <button type="button" className="btn btn-success w-full" style={{ minHeight: 46 }} disabled={submitting || !resolutionNotes.trim()} onClick={() => handleStatusUpdate('resolved')}>
            <CheckCircle size={15} /> Mark resolved
          </button>
        </AppSheet>
      )}

      <div className={isApp ? undefined : 'grid-2'}>
        {/* Left: Details + Timeline */}
        <div>
          {/* Customer & Details */}
          <div className="card mb-4">
            <div className="card-header"><h3>Ticket Details</h3></div>
            <div className="card-body">
              <div style={{ display: 'grid', gap: 12 }}>
                <Row label="Customer" value={<Link to={`/customers/${complaint.customer?._id}`} style={{ color: 'var(--primary)', fontWeight: 600 }}>{complaint.customer?.name}</Link>} />
                {(complaint.customer?.address?.area || complaint.customer?.broadband?.houseNumber || complaint.customer?.catv?.houseNumber) && (
                  <Row label="Society / House No" value={[
                    complaint.customer?.address?.area,
                    complaint.customer?.broadband?.houseNumber || complaint.customer?.catv?.houseNumber,
                  ].filter(Boolean).join(' - ')} />
                )}
                <Row label="Customer ID" value={<span className="font-mono">{complaint.customer?.customerId}</span>} />
                <Row label="Mobile" value={complaint.customer?.mobile} />
                <Row label="Category" value={complaint.category?.replace(/_/g, ' ')} />
                <Row label="SLA Deadline" value={complaint.slaDeadline ? format(new Date(complaint.slaDeadline), 'dd/MM/yyyy HH:mm') : '—'} />
                <Row label="Created" value={format(new Date(complaint.createdAt), 'dd/MM/yyyy HH:mm')} />
                {complaint.resolvedAt && <Row label="Resolved At" value={format(new Date(complaint.resolvedAt), 'dd/MM/yyyy HH:mm')} />}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="card mb-4">
            <div className="card-header"><h3>Description</h3></div>
            <div className="card-body">
              <p style={{ fontSize: '.875rem', color: 'var(--text)', lineHeight: 1.7 }}>{complaint.description}</p>
            </div>
          </div>

          <AttachmentsCard attachments={complaint.attachments} />

          {/* Resolution */}
          {complaint.resolutionNotes && (
            <div className="card mb-4" style={{ borderColor: 'var(--success)' }}>
              <div className="card-header" style={{ background: 'var(--cb-d1fae5)' }}>
                <h3 style={{ color: 'var(--c-065f46)' }}><CheckCircle size={15} style={{ display: 'inline', marginRight: 6 }} />Resolution</h3>
              </div>
              <div className="card-body">
                <p style={{ fontSize: '.875rem' }}>{complaint.resolutionNotes}</p>
                {complaint.customerRemark && (
                  <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                    Sent to customer via WhatsApp: "{complaint.customerRemark}"
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="card">
            <div className="card-header"><h3>Activity Timeline</h3></div>
            <div className="card-body">
              <div className="timeline">
                {(complaint.timeline || []).map((t, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-dot">
                      <MessageSquare size={12} color="var(--c-ffffff)" />
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-action">{t.action}</div>
                      {t.description && <div className="timeline-desc">{t.description}</div>}
                      {t.attachments?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                          {t.attachments.map((url, ai) => (
                            <a key={ai} href={url} target="_blank" rel="noreferrer">
                              <img src={url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="timeline-time">
                        {t.performedBy?.name && <><User size={10} style={{ display: 'inline' }} /> {t.performedBy.name} · </>}
                        {format(new Date(t.timestamp), 'dd/MM/yyyy HH:mm')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add comment */}
              {isStaffOrAdmin && (
                <form onSubmit={handleComment} style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <div className="form-group">
                    <label className="form-label">Add Comment</label>
                    <textarea className="form-control" rows={3} value={comment}
                      onChange={e => setComment(e.target.value)} placeholder="Add a note or update..." />
                  </div>
                  <ImagePicker images={commentImages} setImages={setCommentImages} />
                  <button type="submit" className="btn btn-secondary btn-sm" disabled={submitting || !comment.trim()}>
                    Add Comment
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        {isStaffOrAdmin && !isApp && (
          <div>
            {/* Assign */}
            <div className="card mb-4">
              <div className="card-header"><h3>Assignment</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label className="form-label">Assign to Staff</label>
                  <select className="form-control" value={complaint.assignedTo?._id || ''}
                    onChange={e => handleAssign(e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {staff.map(s => <option key={s._id} value={s._id}>{s.name} ({s.role})</option>)}
                  </select>
                </div>
                {complaint.assignedTo && (
                  <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    Currently: <strong>{complaint.assignedTo.name}</strong>
                    {complaint.assignedAt && <span> · {format(new Date(complaint.assignedAt), 'dd/MM/yy')}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Status update */}
            {!['resolved', 'closed'].includes(complaint.status) && (
              <div className="card mb-4">
                <div className="card-header"><h3>Update Status</h3></div>
                <div className="card-body">
                  <div className="form-group">
                    <label className="form-label">Resolution Notes</label>
                    <textarea className="form-control" rows={3} value={resolutionNotes}
                      onChange={e => setResolutionNotes(e.target.value)}
                      placeholder="Internal note — describe what was done to resolve the issue..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message to Customer (sent via WhatsApp)</label>
                    <textarea className="form-control" rows={2} value={customerRemark}
                      onChange={e => setCustomerRemark(e.target.value)}
                      placeholder="e.g. Your connection issue has been fixed. Please check and let us know if it persists." />
                  </div>
                  <ImagePicker images={statusImages} setImages={setStatusImages} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {complaint.status === 'open' && (
                      <button className="btn btn-warning btn-sm" onClick={() => handleStatusUpdate('in_progress')} disabled={submitting}>
                        Mark In Progress
                      </button>
                    )}
                    {['open', 'in_progress'].includes(complaint.status) && (
                      <button className="btn btn-success btn-sm" onClick={() => handleStatusUpdate('resolved')} disabled={submitting || !resolutionNotes}>
                        <CheckCircle size={13} /> Mark Resolved
                      </button>
                    )}
                    {complaint.status === 'resolved' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleStatusUpdate('closed')} disabled={submitting}>
                        Close Ticket
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Customer Info Quick View */}
            <div className="card">
              <div className="card-header"><h3>Customer Quick Info</h3></div>
              <div className="card-body">
                <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 12 }}>
                  {complaint.customer?.address?.line1}, {complaint.customer?.address?.area}
                </p>
                <Link to={`/customers/${complaint.customer?._id}`} className="btn btn-outline btn-sm w-full" style={{ justifyContent: 'center' }}>
                  View Full Customer Profile
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
    <span style={{ fontSize: '.78rem', color: 'var(--text-muted)', flexShrink: 0, marginRight: 8 }}>{label}</span>
    <span style={{ fontSize: '.85rem', fontWeight: 500, textAlign: 'right', textTransform: 'capitalize' }}>{value || '—'}</span>
  </div>
);
